import { useEffect, useState, useMemo } from "react";
import { CustomerProfileTable } from "../components/ReportTable";
import { customerProfileColumns } from "../config/reportColumns";
import DashboardLayout from "../layouts/DashboardLayout";
import SearchableDropdown from "../components/SearchableDropdown";
import {
    fetchRegions,
    fetchParties,
    fetchCustomerProfileReport,
    fetchTerritories,
} from "../services/customerProfileService";
import ExcelExport from "../components/ExcelExport";

const CustomerProfile = ({ onLogout }) => {
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [region, setRegion] = useState([]);
    const [party, setParty] = useState([]);
    const [territory, setTerritory] = useState([]);
    const [paymentType, setPaymentType] = useState("");

    const [regions, setRegions] = useState([]);
    const [parties, setParties] = useState([]);
    const [territories, setTerritories] = useState([]);

    const paymentTypeOptions = [
        { value: "Cash", label: "Cash" },
        { value: "Credit", label: "Credit" }
    ];

    const [loading, setLoading] = useState(false);
    const [totalsPerProduct, setTotalsPerProduct] = useState([]);
    const [paymentHistory, setPaymentHistory] = useState([]);

    // Load regions on mount
    useEffect(() => {
        fetchRegions()
            .then((res) => setRegions(res.map((r) => ({ value: r, label: r }))))
            .catch((err) => console.error(err));
    }, []);

    // Load parties whenever region or territory changes
    useEffect(() => {
        if (region.length === 0) {
            setParties([]);
            return;
        }

        const regionStr = region.join(',');
        const territoryStr = territory.join(',');

        fetchParties(regionStr, territoryStr)
            .then((res) => setParties(res.map((p) => ({ value: p, label: p }))))
            .catch((err) => console.error(err));

        setParty([]);
    }, [region, territory]);

    // Load territories whenever region changes
    useEffect(() => {
        if (region.length === 0) {
            setTerritories([]);
            return;
        }

        const regionStr = region.join(',');

        fetchTerritories(regionStr)
            .then((res) => setTerritories(res.map((t) => ({ value: t, label: t }))))
            .catch((err) => console.error(err));

        setTerritory([]);
    }, [region]);

    const applyFilter = async () => {
        if (!fromDate || !toDate) {
            alert("From Date and To Date are required");
            return;
        }

        setLoading(true);

        try {
            const res = await fetchCustomerProfileReport({
                fromDate,
                toDate,
                party: party.length > 0 ? party.join(',') : "",
                region: region.length > 0 ? region.join(',') : "",
                territory: territory.length > 0 ? territory.join(',') : "",
                searchText,
                paymentType,
            });

            console.log("API Response:", res);

            // Sales data
            const salesRaw = res?.data?.details || [];

            // Totals map by product
            const totalsMap = {};
            (res?.data?.totals_per_product || []).forEach((t) => {
                const key = `${t.bill_no}|${t.bill_type}|${t.bill_date}|${t.products_name}`;
                totalsMap[key] = t;
            });

            // Merge totals into each row
            const salesDetails = salesRaw.map((row) => {
                const key = `${row.bill_no}|${row.bill_type}|${row.bill_date}|${row.products_name}`;
                const totals = totalsMap[key] || {};
                return {
                    ...row,
                    total_quantity: totals.total_quantity || 0,
                    total_free_qty: totals.total_free_qty || 0,
                    total_alt_qty: totals.total_alt_qty || 0,
                };
            });

            // Payment history is now LEDGERWISE (grouped by party_name from backend)
            const paymentDetails = [];
            (res?.payment_history?.details || []).forEach((paymentGroup) => {
                const partyName = paymentGroup.party_name;
                if (paymentGroup.payments && Array.isArray(paymentGroup.payments)) {
                    paymentGroup.payments.forEach((payment) => {
                        paymentDetails.push({
                            party_name: partyName,
                            bill_no: payment.invoice_no || "",
                            payment_date: payment.payment_date,
                            paid_amount: payment.paid_amount,
                            receipt_no: payment.receipt_no || "",
                            opening_balance: payment.opening_balance,
                            closing_balance: payment.closing_balance,
                            payment_type: payment.payment_type,
                            discount_amount: "", // Not available in API
                        });
                    });
                }
            });

            console.log("Payment Details (ledgerwise):", paymentDetails);

            setData(salesDetails);
            setPaymentHistory(paymentDetails);
            setTotalsPerProduct(res?.data?.totals_per_product || []);

        } catch (err) {
            console.error(err);
            alert("Failed to load report");
        } finally {
            setLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        return (Array.isArray(data) ? data : []).filter((row) => {
            if (paymentType) {
                const rowPaymentType = String(row.cash_credit || row.bill_type || "").toLowerCase();
                if (!rowPaymentType.includes(paymentType.toLowerCase())) {
                    return false;
                }
            }
            if (!searchText) return true;
            const search = searchText.toLowerCase();
            return (
                row.products_name?.toLowerCase().includes(search) ||
                row.pack_sizes?.toLowerCase().includes(search) ||
                row.bill_no?.toLowerCase().includes(search) ||
                row.bill_type?.toLowerCase().includes(search)
            );
        });
    }, [data, searchText, paymentType]);

    const getExportData = () => {
        // Payment map grouped LEDGERWISE by party_name
        const paymentMap = {};
        paymentHistory.forEach((p) => {
            const partyName = p.party_name;
            if (partyName) {
                if (!paymentMap[partyName]) paymentMap[partyName] = [];
                paymentMap[partyName].push(p);
            }
        });

        const totalsMap = totalsPerProduct.reduce((acc, t) => {
            const key = `${t.bill_no}|${t.bill_type}|${t.bill_date}|${t.products_name}`;
            acc[key] = t;
            return acc;
        }, {});

        const grouped = {};
        const seenParties = new Set();

        filteredData.forEach((row) => {
            const key = `${row.bill_no}|${row.bill_type}|${row.bill_date}|${row.products_name}`;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(row);
        });

        const exportRows = [];

        Object.keys(grouped).forEach((groupKey) => {
            const salesRows = grouped[groupKey];
            const totals = totalsMap[groupKey] || {};
            const partyName = salesRows[0]?.party_name;
            const isFirstPartyOccurrence = partyName ? !seenParties.has(partyName) : false;

            if (partyName && isFirstPartyOccurrence) {
                seenParties.add(partyName);
            }

            const payments = isFirstPartyOccurrence ? (paymentMap[partyName] || []) : [];
            const maxRows = Math.max(salesRows.length, payments.length);

            for (let i = 0; i < maxRows; i++) {
                const sale = salesRows[i] || {};
                const pay = payments[i] || {};

                exportRows.push({
                    ...sale,
                    total_quantity: i === 0 ? totals.total_quantity : "",
                    total_free_qty: i === 0 ? totals.total_free_qty : "",
                    total_alt_qty: i === 0 ? totals.total_alt_qty : "",
                    grand_total: i === 0 ? totals.total_amount : "",
                    payment_date: isFirstPartyOccurrence && i === 0 ? pay.payment_date || "" : "",
                    paid_amount: isFirstPartyOccurrence && i === 0 ? pay.paid_amount || "" : "",
                    discount_amount: isFirstPartyOccurrence && i === 0 ? pay.discount_amount || "" : "",
                    receipt_no: isFirstPartyOccurrence && i === 0 ? pay.receipt_no || "" : "",
                    opening_balance: isFirstPartyOccurrence && i === 0 ? pay.opening_balance || "" : "",
                    closing_balance: isFirstPartyOccurrence && i === 0 ? pay.closing_balance || "" : "",
                });
            }
        });

        return exportRows;
    };

    return (
        <DashboardLayout onLogout={onLogout}>
            <div className="flex">
                <main className="flex-1 p-1 bg-gray-100 h-screen overflow-hidden">

                    <div className="flex justify-between items-center mb-2">
                        <h1 className="text-2xl font-bold">
                            Party Profile Report
                        </h1>

                        <ExcelExport
                            data={getExportData()}
                            columns={customerProfileColumns}
                            reportTitle="Customer Profile Report"
                            fileName="customer_profile_report.xlsx"
                            reportFilters={{
                                fromDate,
                                toDate,
                                region,
                                territory,
                                party,
                                "Payment Type": paymentType,
                            }}
                            groupKeyFields={["bill_date", "bill_no", "bill_type", "products_name"]}
                            hiddenRepeatKeys={[
                                "bill_no",
                                "products_name",
                                "bill_type",
                                "bill_date",
                                "total_quantity",
                                "total_free_qty",
                                "total_alt_qty",
                                "grand_total",
                            ]}
                        />
                    </div>

                    <div className="flex flex-wrap items-end gap-2 mb-1 px-1">
                        <input
                            type="text"
                            placeholder="Search Product..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-full md:w-60 border rounded px-2 py-1"
                        />

                        <div className="flex gap-1 items-center">
                            <label>From:</label>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="border rounded px-2 py-1"
                            />
                        </div>

                        <div className="flex gap-1 items-center">
                            <label>To:</label>
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="border rounded px-2 py-1"
                            />
                        </div>

                        <div className="w-40">
                            <SearchableDropdown
                                label="Region"
                                options={regions}
                                value={region}
                                onChange={setRegion}
                                isMulti={true}
                            />
                        </div>

                        <div className="w-40">
                            <SearchableDropdown
                                label="Territory"
                                options={territories}
                                value={territory}
                                onChange={setTerritory}
                                disabled={region.length === 0}
                                isMulti={true}
                            />
                        </div>

                        <div className="w-40">
                            <SearchableDropdown
                                label="Party"
                                options={parties}
                                value={party}
                                onChange={setParty}
                                disabled={regions.length === 0 && region.length === 0}
                                isMulti={true}
                            />
                        </div>

                        <div className="w-40">
                            <SearchableDropdown
                                options={paymentTypeOptions}
                                value={paymentType}
                                onChange={setPaymentType}
                                placeholder="Cash/Credit"
                            />
                        </div>

                        <button
                            onClick={applyFilter}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 whitespace-nowrap"
                        >
                            {loading ? "Loading..." : "Apply"}
                        </button>
                    </div>

                    <CustomerProfileTable
                        columns={customerProfileColumns}
                        data={filteredData}
                        paymentHistory={paymentHistory}
                        totalsPerProduct={totalsPerProduct}
                    />

                </main>
            </div>
        </DashboardLayout>
    );
};

export default CustomerProfile;